// src/hooks/useUnreadChats.js
//
// Live unread-conversation count for the navbar Chats badge, plus a soft
// notification chime when a NEW creator message lands.
//
// Data model (same rule as RequestGate.isUnread — the chat rail's dot):
// a conversation is unread when its last message came from the creator
// and the fan hasn't opened the thread since (user_last_read_at is null
// or older than last_message_at).
//
// Signals:
//   1. Supabase realtime on `conversations` filtered to this user —
//      an incoming message bumps last_message_at (count up + chime);
//      markRead bumps user_last_read_at (count down, NO chime).
//   2. window focus refetch + 60s poll as safety nets.
//
// CHIME RULES (deliberate):
//   - Only when a conversation's last_message_at advances PAST the max
//     we've already seen — markRead updates and refetches never chime,
//     and the initial load never chimes (stale unread ≠ news).
//   - Throttled to one chime per 4s (a burst of AI messages is one event).
//   - Suppressed while the user is already on /chats — the thread UI is
//     the notification there.
//   - WebAudio two-note blip, no asset. Browsers suspend AudioContext
//     until a user gesture; if it won't resume we skip silently rather
//     than queueing sounds for a jump-scare later.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/AuthService';
import { isUnread } from '../components/RequestGate';

const POLL_MS = 60_000;
const CHIME_THROTTLE_MS = 4_000;

// ---------------------------------------------------------------------
// SOUND
// ---------------------------------------------------------------------
let sharedCtx = null;

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    sharedCtx = sharedCtx || new AC();
    if (sharedCtx.state === 'suspended') {
      sharedCtx.resume().catch(() => { /* no gesture yet — skip */ });
      if (sharedCtx.state === 'suspended') return;
    }
    const t = sharedCtx.currentTime;
    // Two quick ascending sine notes (A5 → E6), quiet and short.
    [880, 1318.5].forEach((freq, i) => {
      const osc = sharedCtx.createOscillator();
      const gain = sharedCtx.createGain();
      const at = t + i * 0.09;
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.05, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.32);
      osc.connect(gain).connect(sharedCtx.destination);
      osc.start(at);
      osc.stop(at + 0.36);
    });
  } catch { /* audio unavailable — badge still works */ }
}

// ---------------------------------------------------------------------
// HOOK
// ---------------------------------------------------------------------
export function useUnreadChats(userId) {
  const [count, setCount] = useState(0);
  // Newest creator-message timestamp we've already accounted for. Chime
  // only when something arrives BEYOND this — never on load or markRead.
  const seenMaxRef = useRef(0);
  const primedRef = useRef(false); // first fetch primes seenMax silently
  const lastChimeRef = useRef(0);
  const debounceRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!userId) { setCount(0); return; }
    try {
      // RLS is enforced on conversations — reads go through the
      // SIWE-authed chat-api ('unread' returns the same slim rows the
      // old direct query selected).
      const wallet = authService.getSessionWallet();
      const token = wallet ? authService.getToken(wallet) : null;
      if (!token) { setCount(0); return; }
      const { data: res, error } = await supabase.functions.invoke('chat-api', {
        body: { action: 'unread' },
        headers: { 'X-Lunexx-Auth': `Bearer ${token}` },
      });
      if (error || res?.error) return;
      const rows = res?.conversations || [];
      setCount(rows.filter(isUnread).length);
      // Advance the seen watermark on every fetch — fetches never chime.
      for (const r of rows) {
        const ts = new Date(r.last_message_at || 0).getTime();
        if (Number.isFinite(ts) && ts > seenMaxRef.current) seenMaxRef.current = ts;
      }
      primedRef.current = true;
    } catch (e) {
      console.warn('[unread-chats] fetch failed:', e);
    }
  }, [userId]);

  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      fetchCount();
    }, 300);
  }, [fetchCount]);

  // Initial load + user switch
  useEffect(() => {
    seenMaxRef.current = 0;
    primedRef.current = false;
    fetchCount();
  }, [fetchCount]);

  // Realtime: any change to my conversations → recount; chime only on a
  // genuinely NEW creator message.
  useEffect(() => {
    if (!userId) return;
    // BROADCAST bumps from the server-side chat writers (chatBroadcast.ts)
    // — postgres_changes on conversations dies under RLS. Bump payloads
    // carry last_message_sender/last_message_at, which is exactly what
    // the chime gate below reads.
    const channel = supabase
      .channel(`convlist:${userId}`)
      .on(
        'broadcast',
        { event: 'bump' },
        (payload) => {
          const row = payload?.payload;
          if (row && primedRef.current) {
            const ts = new Date(row.last_message_at || 0).getTime();
            const isNews =
              row.last_message_sender === 'creator' &&
              isUnread(row) &&
              Number.isFinite(ts) &&
              ts > seenMaxRef.current;
            if (isNews) {
              seenMaxRef.current = ts;
              const now = Date.now();
              const onChats = window.location.pathname === '/chats';
              if (!onChats && now - lastChimeRef.current > CHIME_THROTTLE_MS) {
                lastChimeRef.current = now;
                playChime();
              }
            }
          }
          debouncedFetch();
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId, debouncedFetch]);

  // Safety nets: focus refetch + slow poll (realtime can drop silently)
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    const interval = setInterval(fetchCount, POLL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [userId, fetchCount]);

  return count;
}

export default useUnreadChats;
